import React, { useState, useEffect } from 'react';
import { Form, Button, Row, Col, Alert, Spinner, Table, Modal, Badge } from 'react-bootstrap';
import axios from 'axios';
import config from '../../config';
import { FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import { useHasPermission, PERMISSIONS } from '../../utils/permissions';

const PaymentTermsTab = () => {
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showModal, setShowModal] = useState(false);
  const [editingTerm, setEditingTerm] = useState(null);

  // Permissions
  const canCreate = useHasPermission(PERMISSIONS.SETTINGS_EDIT);
  const canEdit = useHasPermission(PERMISSIONS.SETTINGS_EDIT);
  const canDelete = useHasPermission(PERMISSIONS.SETTINGS_EDIT);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    // days: 0
  });

  useEffect(() => {
    fetchPaymentTerms();
  }, []);

  const fetchPaymentTerms = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${config.API_BASE_URL}/payment-terms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPaymentTerms(response.data);
    } catch (error) {
      console.error('Error fetching payment terms:', error);
      setMessage({ type: 'danger', text: 'Failed to load payment terms' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', days: 0 });
    setEditingTerm(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      const data = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        // days: parseInt(formData.days) || 0
      };

      if (editingTerm) {
        // Update existing term
        await axios.put(`${config.API_BASE_URL}/payment-terms/${editingTerm._id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMessage({ type: 'success', text: 'Payment term updated successfully!' });
      } else {
        // Create new term
        await axios.post(`${config.API_BASE_URL}/payment-terms`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMessage({ type: 'success', text: 'Payment term created successfully!' });
      }

      setShowModal(false);
      resetForm();
      fetchPaymentTerms();
    } catch (error) {
      console.error('Error saving payment term:', error);
      const errorMessage = error.response?.data?.message || 'Failed to save payment term';
      setMessage({ type: 'danger', text: errorMessage });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (term) => {
    setEditingTerm(term);
    setFormData({
      name: term.name,
      description: term.description || '',
      days: term.days || 0
    });
    setShowModal(true);
  };

  const handleDelete = async (term) => {
    if (!window.confirm(`Are you sure you want to deactivate "${term.name}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${config.API_BASE_URL}/payment-terms/${term._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMessage({ type: 'success', text: 'Payment term deactivated successfully!' });
      fetchPaymentTerms();
    } catch (error) {
      console.error('Error deleting payment term:', error);
      setMessage({ type: 'danger', text: 'Failed to deactivate payment term' });
    }
  };

  const handleSeedDefaults = async () => {
    if (!window.confirm('This will create default payment terms if they don\'t already exist. Continue?')) {
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${config.API_BASE_URL}/payment-terms/seed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMessage({ type: 'success', text: 'Default payment terms created successfully!' });
      fetchPaymentTerms();
    } catch (error) {
      console.error('Error seeding payment terms:', error);
      setMessage({ type: 'danger', text: 'Failed to create default payment terms' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading payment terms...</p>
      </div>
    );
  }

  return (
    <div className="payment-terms-tab">
      {message.text && (
        <Alert variant={message.type} dismissible onClose={() => setMessage({ type: '', text: '' })}>
          {message.text}
        </Alert>
      )}

      <Row className="mb-3">
        <Col>
          <h5>Payment Terms Management</h5>
        </Col>
        <Col className="text-end">
          {canCreate && (
            <>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleSeedDefaults}
                disabled={saving}
                className="me-2"
              >
                Seed Defaults
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowModal(true)}
                disabled={saving}
              >
                <FaPlus size={16} className="me-1" />
                Add Payment Term
              </Button>
            </>
          )}
        </Col>
      </Row>

      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>Payment Term</th>
            <th>Description</th>
            {/* <th>Days</th> */}
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {paymentTerms.length === 0 ? (
            <tr>
              <td colSpan="5" className="text-center py-4">
                No payment terms found. Create some payment terms to get started.
              </td>
            </tr>
          ) : (
            paymentTerms.map(term => (
              <tr key={term._id}>
                <td>{term.name}</td>
                <td>{term.description || '-'}</td>
                {/* <td>{term.days === 0 ? 'Advance' : `${term.days} days`}</td> */}
                <td>
                  <Badge bg={term.isActive ? 'success' : 'secondary'}>
                    {term.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </td>
                <td>
                  {canEdit && (
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleEdit(term)}
                      className="me-1"
                    >
                      <FaEdit size={14} />
                    </Button>
                  )}
                  {canDelete && (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDelete(term)}
                    >
                      <FaTrash size={14} />
                    </Button>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      {/* Modal for Add/Edit */}
      <Modal show={showModal} onHide={() => { setShowModal(false); resetForm(); }}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingTerm ? 'Edit Payment Term' : 'Add Payment Term'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Payment Term *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="e.g., 30 Days"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Optional description..."
                  />
                </Form.Group>
              </Col>
            </Row>
            {/* <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Days</Form.Label>
                  <Form.Control
                    type="number"
                    value={formData.days}
                    onChange={(e) => handleInputChange('days', e.target.value)}
                    placeholder="0 for advance payment"
                    min="0"
                  />
                  <Form.Text className="text-muted">
                    Enter 0 for advance, positive number for credit days
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row> */}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => { setShowModal(false); resetForm(); }}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={saving || !formData.name.trim()}
            >
              {saving ? 'Saving...' : (editingTerm ? 'Update' : 'Create')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default PaymentTermsTab;
